import { GenericField, GenericInput } from '@/ui'
import ctl from '@netlify/classnames-template-literals'
import { ReactNode } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { SearchFilter } from '../'

export interface SuggestionFieldProps<T extends Record<string, unknown>> {
	label: string
	values: Array<T>
	onChange: (values: Array<T>) => void
	search: string
	onSearchChange: (search: string) => void
	suggestions: Array<T>
	hint?: ReactNode
	placeholder?: string
	renderValue: (value: T) => ReactNode
	renderSuggestion?: (value: T) => ReactNode
	numericEditorAccessor?: (value: T) => number
	numericEditorMutator?: (value: number, original: T) => T
	numericEditorSuffix?: string
	numericLimits?: [number, number]
	isLoading?: boolean
	isGenerating?: boolean
	isDisabled?: boolean
	isWaiting?: boolean
	emptyMessage?: string
	noSuggestionsMessage?: string
	hasError?: boolean
	errorMessage?: string
	getKey?: (item: T) => string
}

export function SuggestionField<T extends Record<string, unknown>>(
	{
		label,
		values,
		search,
		onSearchChange,
		suggestions = [],
		onChange,
		hint,
		placeholder,
		renderValue,
		renderSuggestion,
		numericEditorAccessor,
		numericEditorMutator,
		numericEditorSuffix,
		numericLimits,
		isLoading = false,
		isGenerating = false,
		isDisabled = false,
		isWaiting = false,
		emptyMessage,
		noSuggestionsMessage,
		hasError = false,
		errorMessage,
		getKey,
	}: SuggestionFieldProps<T>,
) {
	const { t } = useTranslation('common')
	const resolvedEmptyMessage = emptyMessage ?? t('suggestions.noResults')
	const resolvedErrorMessage = errorMessage ?? t('suggestions.loadError')
	const selectedKeys = getKey ? new Set(values.map(getKey)) : null

	const handleNumericChange = (value: number, i: number) => {
		const mutated = numericEditorMutator?.(value, values[i])
		if (mutated) {
			onChange(values.map((v, j) => (j === i ? mutated : v)))
		}
	}

	return (
		<>
			<GenericField label={label} hint={hint} wrapLabel={false}>
				<GenericInput
					isGenerating={isGenerating}
					isLoading={isLoading}
					isDisabled={isDisabled}
					isWaiting={isWaiting}
				>
					{({ className }) => (
						<div className={ctl(`${className}`)}>
							<div className="min-h-[4rem]">
								{(values.length === 0 && placeholder) && (
									<div className="text-grey-500 italic">{placeholder}</div>
								)}
								{values.map((value, i, values) => (
									<div
										key={i}
										className="inline-flex items-center gap-2 bg-blue-50 rounded ring ring-blue-200 mr-2 pl-4 mb-2"
									>
										{renderValue(value)}
										{numericEditorAccessor && (
											<>
												<input
													type="number"
													className="[field-sizing:content] min-w-1 bg-white"
													min={numericLimits?.[0]}
													max={numericLimits?.[1]}
													value={numericEditorAccessor(value)}
													onChange={(e) => handleNumericChange(Number(e.target.value), i)}
												/>

												{numericEditorSuffix && <>{numericEditorSuffix}</>}
											</>
										)}
										<button
											className="cursor-pointer pl-1 py-1 pr-3 whitespace-nowrap"
											onClick={() => onChange(values.filter((_, j) => j !== i))}
										>
											&times;
										</button>
									</div>
								))}
							</div>
							<div className="border-t border-grey-100 pt-2">
								<div>
									<SearchFilter value={search} onChange={onSearchChange} />
								</div>
								{hasError
									? (
										<div className="py-1 text-red-500 text-sm">
											{resolvedErrorMessage}
										</div>
									)
									: isLoading
									? (
										<div className="py-1 text-grey-500 text-sm">
											{t('suggestions.loading')}
										</div>
									)
									: suggestions.length > 0
									? (
										<div className="py-1">
											{suggestions.map((suggestion, i) => {
												const key = getKey ? getKey(suggestion) : i
												const isSelected = selectedKeys
													? selectedKeys.has(key as string)
													: false
												const label = (renderSuggestion ?? renderValue)(suggestion)

												return isSelected
													? (
														<span
															key={key}
															className="inline-flex items-center gap-1 bg-blue-50 rounded ring ring-blue-200 p-2 whitespace-nowrap mr-1 mb-1"
														>
															<svg
																aria-hidden="true"
																className="w-4 h-4 text-blue-500"
																viewBox="0 0 20 20"
																fill="currentColor"
															>
																<path
																	fillRule="evenodd"
																	d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
																	clipRule="evenodd"
																/>
															</svg>
															{label}
														</span>
													)
													: (
														<button
															key={key}
															className="inline-block cursor-pointer bg-grey-50 rounded ring ring-grey-200 p-2 whitespace-nowrap mr-1 mb-1"
															onClick={() => onChange([...values, suggestion])}
														>
															{label}
														</button>
													)
											})}
										</div>
									)
									: search.length > 0
									? (
										<div className="py-1 text-grey-500 text-sm">
											{resolvedEmptyMessage}
										</div>
									)
									: null}
							</div>
						</div>
					)}
				</GenericInput>
			</GenericField>
		</>
	)
}
